import React, { useState } from "react";
import { MoreVertical, Share2, Trash2, Copy, Loader2, Pencil } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

/**
 * Overflow action menu for list rows.
 *
 * Collapses secondary actions (Edit, Share, Delete, Copy) behind a ⋮ button so
 * the row stays scannable. Primary actions (Favorite, Pin, Archive) stay inline
 * via FavPinButtons.
 *
 * Props:
 *   onEdit       — optional; opens an edit sheet
 *   onShare      — async fn or omitted (hides item)
 *   onDelete     — async fn; called after the toast countdown elapses without "Undo"
 *   onCopy       — optional (used by passwords)
 *   deleteLabel  — label + toast text ("Delete note", "Delete reminder", …)
 *   size         — "sm" | "md"  (default md)
 *   testId       — base data-testid (e.g. "note-abc") → becomes "actions-note-abc"
 */
export default function RowActions({
  onEdit,
  onShare,
  onDelete,
  onCopy,
  deleteLabel = "Delete",
  size = "md",
  testId = "row",
  className,
}) {
  const [busy, setBusy] = useState(false);
  const btn = size === "sm" ? "h-7 w-7" : "h-8 w-8";
  const icon = size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4";

  const handleDelete = () => {
    if (busy || !onDelete) return;
    let undone = false;
    toast(deleteLabel, {
      description: "Undo within 5 seconds",
      duration: 5000,
      action: {
        label: "Undo",
        onClick: () => { undone = true; toast.success("Restored"); },
      },
    });
    setTimeout(async () => {
      if (undone) return;
      setBusy(true);
      try { await onDelete(); } finally { setBusy(false); }
    }, 5100);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="More actions"
          title="More actions"
          className={cn(
            "inline-flex items-center justify-center rounded-full transition active:scale-95",
            btn,
            "hover:bg-muted text-muted-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
            className
          )}
          data-testid={`actions-${testId}`}
          onClick={(e) => e.stopPropagation()}
        >
          {busy ? <Loader2 className={cn(icon, "animate-spin")} /> : <MoreVertical className={icon} />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44" onCloseAutoFocus={(e) => e.preventDefault()}>
        {onEdit && (
          <DropdownMenuItem
            onSelect={(e) => { e.preventDefault(); onEdit?.(); }}
            data-testid={`action-edit-${testId}`}
          >
            <Pencil className="h-4 w-4 mr-2" />
            Edit
          </DropdownMenuItem>
        )}
        {onShare && (
          <>
            <DropdownMenuItem
              onSelect={(e) => { e.preventDefault(); onShare?.(); }}
              data-testid={`action-share-${testId}`}
            >
              <Share2 className="h-4 w-4 mr-2" />
              Share
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        {onCopy && (
          <DropdownMenuItem
            onSelect={(e) => { e.preventDefault(); onCopy?.(); }}
            data-testid={`action-copy-${testId}`}
          >
            <Copy className="h-4 w-4 mr-2" />
            Copy
          </DropdownMenuItem>
        )}
        {onDelete && (
          <DropdownMenuItem
            onSelect={(e) => { e.preventDefault(); handleDelete(); }}
            className="text-destructive focus:text-destructive focus:bg-destructive/10"
            data-testid={`action-delete-${testId}`}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
